import React, { memo, useMemo, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Tabs, Tab, Typography, Divider, Link as MuiLink } from "@mui/material";
import { FIELD_TYPES } from "../custom-component/fieldHelpers";

/**
 * TabsManager
 *
 * Renders tabs dynamically from `tabs` prop (typically loaded from DB json/jsonb column like `tabs_data`).
 *
 * ------------------------------------------------------------------
 * ✅ Expected `tabs` prop / DB json shape (tabs_data)
 * ------------------------------------------------------------------
 * tabs = [
 *   {
 *     id: "overview",                 // required (string) unique
 *     label: "Overview",              // required (string) tab label
 *     fields: [                       // required (array) tab content
 *       {
 *         type: "text",               // FIELD_TYPES.TEXT
 *         id: "t1",                   // optional (string) recommended
 *         label: "About",             // optional (string)
 *         value: "Some text...",      // optional (string)
 *         variant: "body1",           // optional (MUI Typography variant)
 *         fontWeight: 600,            // optional (number)
 *         sx: { color: "text.primary" } // optional (style object)
 *       },
 *       { type: "divider", id: "d1" }, // FIELD_TYPES.DIVIDER
 *       {
 *         type: "list",               // FIELD_TYPES.LIST
 *         id: "l1",
 *         label: "Requirements",
 *         ordered: false,             // boolean (true => ol, false => ul)
 *         items: ["Item 1", "Item 2"] // array of strings
 *       },
 *       {
 *         type: "rich_list",          // FIELD_TYPES.RICH_LIST
 *         ordered: true,
 *         items: [
 *           {
 *             runs: [{ text: "Select Start Service", style: "listTitle" }],
 *             children: [
 *               {
 *                 ordered: false,
 *                 items: [{ runs: [{ text: "Begin the registration ...", style: "listBody" }] }]
 *               }
 *             ]
 *           }
 *         ]
 *       },
 *       {
 *         type: "link",               // FIELD_TYPES.LINK
 *         id: "lnk1",
 *         label: "Open portal",
 *         href: "https://example.com",
 *         text: "Go to service portal"
 *       },
 *       {
 *         type: "html",               // FIELD_TYPES.HTML
 *         id: "h1",
 *         value: "<b>Trusted HTML only</b>"
 *       }
 *     ]
 *   }
 * ]
 *
 */
const TabsManager = ({ tabs = [] }) => {
  const [t] = useTranslation();
  const firstTabId = tabs?.[0]?.id || "";
  const [active, setActive] = useState(firstTabId);

  // #region RICH_LIST_STYLES
  // NOTE:
  // - This supports multi-layer lists with custom text styling coming from DB.
  // - For safety, DB-provided sx is sanitized (whitelist keys) before applying.
  // - Prefer using `style` tokens from DB (e.g. "listTitle", "listBody").
  const TEXT_PRESETS = useMemo(
    () => ({
      listTitle: { fontWeight: 700 },
      listBody: { fontWeight: 400 },
      muted: { color: "text.secondary" },
      success: { color: "#1f7a54", fontWeight: 700 },
    }),
    []
  );

  const sanitizeSx = useCallback((sx) => {
    if (!sx || typeof sx !== "object" || Array.isArray(sx)) return undefined;

    const ALLOWED_KEYS = new Set([
      // spacing
      "m", "mt", "mb", "ml", "mr", "mx", "my",
      "p", "pt", "pb", "pl", "pr", "px", "py",
      // typography
      "fontWeight", "fontSize", "lineHeight", "color",
      "textAlign", "textDecoration", "textTransform", "whiteSpace",
    ]);

    return Object.fromEntries(Object.entries(sx).filter(([k]) => ALLOWED_KEYS.has(k)));
  }, []);

  const renderTextRuns = useCallback(
    (runs = []) => {
      if (!Array.isArray(runs) || runs.length === 0) return null;

      return runs.map((run, i) => {
        const preset = run?.style ? TEXT_PRESETS[run.style] : undefined;
        const safeSx = sanitizeSx(run?.sx);

        return (
          <Typography
            key={`${run?.id || i}`}
            component="span"
            variant={run?.variant || "body1"}
            sx={{ ...(preset || {}), ...(safeSx || {}) }}
          >
            {t(run?.text ?? "")}
          </Typography>
        );
      });
    },
    [TEXT_PRESETS, sanitizeSx, t]
  );

  const renderRichList = useCallback(
    (list, keyPrefix = "rich") => {
      if (!list) return null;

      const ordered = !!list?.ordered;
      const Tag = ordered ? "ol" : "ul";
      const items = Array.isArray(list?.items) ? list.items : [];
      if (!items.length) return null;

      const listSx = sanitizeSx(list?.sx);

      return (
        <Box component={Tag} sx={{ m: 0, pl: 3, ...(listSx || {}) }}>
          {items.map((node, idx) => {
            const nodeKey = node?.id || `${keyPrefix}-${idx}`;
            const nodeSx = sanitizeSx(node?.sx);
            if (typeof node === "string") {
              return (
                <li key={nodeKey}>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    {t(node)}
                  </Typography>
                </li>
              );
            }

            const runs = Array.isArray(node?.runs)
              ? node.runs
              : node?.text
                ? [{ text: t(node.text), style: "listBody" }]
                : [];

            const children = Array.isArray(node?.children) ? node.children : [];
            const titleRuns = runs.filter(r => r?.style === "listTitle");
            const bodyRuns = runs.filter(r => r?.style !== "listTitle");
            return (
              <li key={nodeKey}>
                <Box sx={{ ...(nodeSx || {}) }}>
                  <Box>
                    {!!titleRuns.length && (
                      <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
                        {renderTextRuns(titleRuns)}
                      </Typography>
                    )}

                    {!!bodyRuns.length && (
                      <Typography variant="body1" sx={{ lineHeight: 1.5, mt: 0.25 }}>
                        {renderTextRuns(bodyRuns)}
                      </Typography>
                    )}
                  </Box>

                  {children.map((childList, cIdx) => (
                    <Box key={`${nodeKey}-c-${cIdx}`} sx={{ mt: 0.5 }}>
                      {renderRichList(childList, `${nodeKey}-c-${cIdx}`)}
                    </Box>
                  ))}
                </Box>
              </li>
            );
          })}
        </Box>
      );
    },
    [renderTextRuns, sanitizeSx, t]
  );
  // #endregion

  // Ensure active tab stays valid when tabs change (after fetching)
  useEffect(() => {
    if (!tabs?.length) return;
    const exists = tabs.some((t) => t.id === active);
    if (!exists) setActive(tabs[0].id);
  }, [tabs, active]);

  const activeTab = useMemo(() => tabs.find((t) => t.id === active), [tabs, active]);
  const handleChange = useCallback((_e, val) => setActive(val), []);

  const renderField = (field, idx) => {
    const type = field?.type || FIELD_TYPES.TEXT;
    const key = field?.id || field?.name || `${type}-${idx}`;

    switch (type) {
      case FIELD_TYPES.DIVIDER:
        return <Divider key={key} sx={{ my: 1 }} />;

      case FIELD_TYPES.RICH_LIST: {
        // Multi-layer list with styled text runs (from DB)
        // Shape: { type: 'rich_list', ordered: true/false, items: [...], sx?: {} }
        // Each item node: { runs: [{ text, style?, sx? }], children?: [ { ordered, items }... ], sx?: {} }
        return (
          <Box key={key} sx={field?.sx}>
            {field?.label ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t(field.label)}
              </Typography>
            ) : null}

            {renderRichList(field, key)}
          </Box>
        );
      }

      case FIELD_TYPES.LINK:
        return (
          <Box key={key}>
            {field?.label ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t(field.label)}
              </Typography>
            ) : null}

            <MuiLink href={field?.href || "#"} target="_blank" rel="noopener noreferrer">
              {t(field?.text || field?.value || field?.href || "Open")}
            </MuiLink>
          </Box>
        );

      case FIELD_TYPES.LIST: {
        const Tag = field?.ordered ? "ol" : "ul";
        const items = field?.items || [];
        if (!items.length) return null;

        return (
          <Box key={key}>
            {field?.label ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t(field.label)}
              </Typography>
            ) : null}

            <Box component={Tag} sx={{ m: 0, pl: 3 }}>
              {items.map((x, i) => (
                <li key={`${key}-${i}`}>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    {t(x)}
                  </Typography>
                </li>
              ))}
            </Box>
          </Box>
        );
      }

      case FIELD_TYPES.HTML:
        return <Box key={key} dangerouslySetInnerHTML={{ __html: t(field?.value || "") }} />;

      case FIELD_TYPES.TEXT:
      default:
        return (
          <Box key={key}>
            {field?.label ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t(field.label)}
              </Typography>
            ) : null}

            <Typography
              variant={field?.variant || "body1"}
              sx={{ lineHeight: 1.7, fontWeight: field?.fontWeight, ...(field?.sx || {}) }}
            >
              {t(field?.value) ?? ""}
            </Typography>
          </Box>
        );
    }
  };

  if (!tabs?.length) return null;

  return (
    <Box>
      <Tabs
        value={active}
        onChange={handleChange}
        variant="fullWidth"
        TabIndicatorProps={{ sx: { bgcolor: "#1f7a54", height: 3, borderRadius: 2 } }}
        sx={{
          bgcolor: "#e9e9e9",
          borderRadius: 1.5,
          minHeight: 46,
          px: 1,
          "& .MuiTab-root": { minHeight: 46, textTransform: "none", fontWeight: 600 },
          "& .Mui-selected": { color: "text.primary" },
        }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.id} value={tab.id} label={t(tab.label && tab.label !== "" ? tab.label : tab.id)} />
        ))}
      </Tabs>

      <Box sx={{ p: 2 }}>{(activeTab?.fields || []).map(renderField)}</Box>
    </Box>
  );
};

export default memo(TabsManager);
